import React, { useState, useEffect } from 'react';
import { X, Send, Loader, Eye, Edit, Trash2, MessageSquare, Brain, FileText, BarChart3, FileEdit, Plus } from 'lucide-react';

interface ReasoningBlock {
  question: string;
  description: string;
  sources: string[];
}

interface Consideration {
  description: string;
  source: string;
}

interface CalcBlock {
  id: string;
  type: 'extracted' | 'calculated' | 'estimated' | 'assumption';
  unit: string;
  order: number;
  title: string;
  value: number | string; // Allow both numbers and strings (for ranges like "333-500 millones")
  formula: string;
  image_icon: string;
  description: string;
  source: string;
  source_url: string;
}

interface StatisticData {
  question: string;
  scope: {
    region: string;
    event_window: string;
    timezone: string;
    time_cutoff: string;
  };
  reasoning_blocks: ReasoningBlock[];
  considerations: Consideration[];
  sessionId?: string;
}

interface CalcData {
  blocks: CalcBlock[];
}

interface ArticleData {
  title: string;
  lead: string;
  description: string;
  tags: string[];
  highlights: string[];
  conclusion: string;
  meta_title?: string;
  meta_description?: string;
  keywords?: string;
  category?: string;
  location?: string;
}

interface ResultData {
  id: string;
  location: string;
  language: string;
  slug: string;
  title: string;
  description: string;
  study_flow: string;
  tags: string[];
  author: string;
  authorId?: string;
  status: string;
  data: {
    blocks: CalcBlock[];
    charts: any[];
    segments: any[];
    evolution: string;
  };
  created_at: string;
  updated_at: string;
  meta_title: string;
  meta_description: string;
  keywords: string;
  og_image: string;
  canonical_url: string;
  lead: string;
  image_id: string;
  related_ad_products: any[];
  type: string;
  related_links: any[];
  og_image_alt: string;
  category: string;
  highlights: string[];
  conclusion: string;
  featured: boolean;
  radar: string;
}

interface NewStatisticModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Componente Chat reutilizable
const ChatColumn: React.FC<{
  title: string;
  icon: React.ReactNode;
  chatHistory: any[];
  chatMessage: string;
  setChatMessage: (msg: string) => void;
  onSendMessage: () => void;
  isLoading: boolean;
  sessionId: string | null;
  placeholder: string;
  bgColor?: string;
}> = ({ 
  title, 
  icon, 
  chatHistory, 
  chatMessage, 
  setChatMessage, 
  onSendMessage, 
  isLoading, 
  sessionId, 
  placeholder,
  bgColor = 'bg-gray-50'
}) => (
  <div className={`w-96 flex flex-col ${bgColor}`}>
    {/* Chat Header */}
    <div className="p-4 border-b border-gray-200 bg-white">
      <h4 className="font-semibold text-gray-900 flex items-center gap-2">
        {icon}
        {title}
      </h4>
      <p className="text-xs text-gray-500 mt-1">
        Conversación contextual sobre el análisis
      </p>
      {sessionId && (
        <p className="text-xs text-gray-400 mt-1">
          Sesión: {sessionId.split('_')[1]}
        </p>
      )}
    </div>

    {/* Chat Messages */}
    <div className="flex-1 p-4 overflow-auto">
      {chatHistory.length === 0 ? (
        <div className="text-center text-gray-500 py-8">
          <MessageSquare className="mx-auto h-8 w-8 text-gray-300 mb-2" />
          <p className="text-sm">Haz una pregunta para iniciar la conversación</p>
        </div>
      ) : (
        <div className="space-y-3">
          {chatHistory.map((message, index) => (
            <div key={index} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] p-3 rounded-lg text-sm ${
                message.role === 'user' 
                  ? 'bg-green-600 text-white' 
                  : 'bg-white border border-gray-200 shadow-sm'
              }`}>
                <div className="text-xs font-medium mb-1 opacity-70">
                  {message.role === 'user' ? 'Tú' : 'GPT'}
                </div>
                <div className="whitespace-pre-wrap">
                  {message.content}
                </div>
                
                {/* Action buttons for structured responses */}
                {message.hasAction && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <button
                      onClick={() => handleApplyBlockAction(message)}
                      className={`w-full px-3 py-2 rounded text-xs font-medium ${
                        message.actionType === 'add' 
                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                          : 'bg-orange-600 text-white hover:bg-orange-700'
                      }`}
                    >
                      {message.actionType === 'add' ? '➕ Añadir Bloque' : '✏️ Modificar Bloque'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>

    {/* Chat Input */}
    <div className="p-4 border-t border-gray-200 bg-white">
      <div className="flex gap-2 mb-3">
        <input
          type="text"
          value={chatMessage}
          onChange={(e) => setChatMessage(e.target.value)}
          placeholder={placeholder}
          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-green-500"
          disabled={isLoading}
          onKeyPress={(e) => e.key === 'Enter' && onSendMessage()}
        />
        <button
          onClick={onSendMessage}
          disabled={!chatMessage.trim() || isLoading}
          className="bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700 disabled:opacity-50"
        >
          {isLoading ? <Loader className="w-4 h-4 animate-spin" /> : <Send size={16} />}
        </button>
      </div>
    </div>
  </div>
);

export const NewStatisticModal: React.FC<NewStatisticModalProps> = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState<'question' | 'blocks' | 'calcs' | 'article' | 'result' | 'json'>('question');
  const [question, setQuestion] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [statisticData, setStatisticData] = useState<StatisticData | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [chatHistory, setChatHistory] = useState<any[]>([]);
  const [editingBlockIndex, setEditingBlockIndex] = useState<number | null>(null);
  const [editingCalcBlockIndex, setEditingCalcBlockIndex] = useState<number | null>(null);
  const [gptQuestions, setGptQuestions] = useState<{[key: number]: string}>({});
  const [isGptLoading, setIsGptLoading] = useState<{[key: number]: boolean}>({});
  const [calcGptQuestions, setCalcGptQuestions] = useState<{[key: number]: string}>({});
  const [isCalcGptLoading, setIsCalcGptLoading] = useState<{[key: number]: boolean}>({});
  const [freeChatMessage, setFreeChatMessage] = useState('');
  const [isFreeChatLoading, setIsFreeChatLoading] = useState(false);
  
  // Calcs tab state
  const [calcData, setCalcData] = useState<CalcData | null>(null);
  const [calcSessionId, setCalcSessionId] = useState<string | null>(null);
  const [calcChatHistory, setCalcChatHistory] = useState<any[]>([]);
  
  // Article tab state
  const [articleData, setArticleData] = useState<ArticleData | null>(null);
  const [isGeneratingArticle, setIsGeneratingArticle] = useState(false);
  
  // Result tab state
  const [resultData, setResultData] = useState<ResultData | null>(null);
  const [isGeneratingResult, setIsGeneratingResult] = useState(false);
  const [isSavingStatistic, setIsSavingStatistic] = useState(false);
  const [calcChatMessage, setCalcChatMessage] = useState('');
  const [isCalcChatLoading, setIsCalcChatLoading] = useState(false);
  const [isUpdatingCalcs, setIsUpdatingCalcs] = useState(false);
  const [jsonView, setJsonView] = useState<'reasoning' | 'calc' | 'article'>('reasoning');
  const [authors, setAuthors] = useState<Array<{ id: string; name: string; isDefault: boolean }>>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/authors', { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setAuthors(Array.isArray(data) ? data : []);
      } catch {
        // ignore — fallback to default name
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!isOpen) return null;

  const handleSubmitQuestion = async () => {
    if (!question.trim()) return;
    
    setIsLoading(true);
    try {
      const response = await fetch('/api/assistant/preproduction', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          question: question.trim()
        })
      });

      if (response.ok) {
        const data = await response.json();
        setStatisticData(data);
        setSessionId(data.sessionId);
        setActiveTab('blocks');
        // console.log(`✅ FRONTEND: Received data with session ${data.sessionId}`); // Removed for production
      } else {
        alert('Error al procesar la pregunta');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error de conexión');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteBlock = (index: number) => {
    if (!statisticData) return;
    const newBlocks = statisticData.reasoning_blocks.filter((_, i) => i !== index);
    setStatisticData({
      ...statisticData,
      reasoning_blocks: newBlocks
    });
  };

  const handleEditBlock = (index: number, updatedBlock: ReasoningBlock) => {
    if (!statisticData) return;
    const newBlocks = [...statisticData.reasoning_blocks];
    newBlocks[index] = updatedBlock;
    setStatisticData({
      ...statisticData,
      reasoning_blocks: newBlocks
    });
    setEditingBlockIndex(null);
  };

  const handleGptQuestion = async (blockIndex: number) => {
    const currentQuestion = gptQuestions[blockIndex]?.trim();
    if (!currentQuestion || !statisticData) return;
    
    // Get current block content
    const currentBlock = statisticData.reasoning_blocks[blockIndex];
    
    // Add user message to chat immediately with full block context
    const userMessage = {
      role: 'user',
      content: `Sobre el bloque ${blockIndex + 1}: ${currentQuestion}

📋 **Contenido actual del bloque:**
**Pregunta:** ${currentBlock.question}
**Descripción:** ${currentBlock.description}
**Fuentes:** ${currentBlock.sources.join(', ')}`,
      timestamp: new Date().toLocaleTimeString(),
      type: 'block_question',
      blockIndex
    };
    
    setChatHistory(prev => [...prev, userMessage]);
    
    // Set loading for this specific block
    setIsGptLoading(prev => ({ ...prev, [blockIndex]: true }));
    
    // Add loading message to chat
    const loadingMessage = {
      role: 'assistant',
      content: '⏳ Analizando tu pregunta...',
      timestamp: new Date().toLocaleTimeString(),
      isLoading: true
    };
    
    setChatHistory(prev => [...prev, loadingMessage]);
    
    try {
      const response = await fetch('/api/gpt/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          sessionId,
          blockIndex,
          question: currentQuestion
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Remove loading message and add real response
        setChatHistory(prev => {
          const withoutLoading = prev.filter(msg => !msg.isLoading);
          
          if (data.hasStructuredResponse) {
            // GPT wants to modify the block
            return [...withoutLoading, {
              role: 'assistant',
              content: data.chatResponse,
              timestamp: new Date().toLocaleTimeString(),
              hasAction: true,
              actionType: 'modify',
              blockIndex: blockIndex,
              blockData: data.blockData
            }];
          } else {
            // Normal conversational response about the block
            return [...withoutLoading, {
              role: 'assistant',
              content: data.response || data.chatResponse,
              timestamp: new Date().toLocaleTimeString()
            }];
          }
        });
        
        // Clear the question for this specific block
        setGptQuestions(prev => ({ ...prev, [blockIndex]: '' }));
        
        // console.log(`✅ CHAT: Added GPT response for block ${blockIndex}`); // Removed for production
      } else {
        // Remove loading and add error
        setChatHistory(prev => {
          const withoutLoading = prev.filter(msg => !msg.isLoading);
          return [...withoutLoading, {
            role: 'assistant',
            content: '❌ Error al procesar tu pregunta. Inténtalo de nuevo.',
            timestamp: new Date().toLocaleTimeString()
          }];
        });
      }
    } catch (error) {
      console.error('Error:', error);
      // Remove loading and add error
      setChatHistory(prev => {
        const withoutLoading = prev.filter(msg => !msg.isLoading);
        return [...withoutLoading, {
          role: 'assistant',
          content: '❌ Error de conexión. Inténtalo de nuevo.',
          timestamp: new Date().toLocaleTimeString()
        }];
      });
    } finally {
      // Remove loading for this specific block
      setIsGptLoading(prev => ({ ...prev, [blockIndex]: false }));
    }
  };

  const handleFreeChatMessage = async () => {
    if (!freeChatMessage.trim() || !sessionId) return;
    
    // Add user message to chat immediately
    const userMessage = {
      role: 'user',
      content: freeChatMessage.trim(),
      timestamp: new Date().toLocaleTimeString(),
      type: 'free_chat'
    };
    
    setChatHistory(prev => [...prev, userMessage]);
    
    // Clear input and set loading
    const messageToSend = freeChatMessage.trim();
    setFreeChatMessage('');
    setIsFreeChatLoading(true);
    
    // Add loading message to chat
    const loadingMessage = {
      role: 'assistant',
      content: '⏳ Pensando...',
      timestamp: new Date().toLocaleTimeString(),
      isLoading: true
    };
    
    setChatHistory(prev => [...prev, loadingMessage]);
    
    try {
      const response = await fetch('/api/gpt/free-chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          sessionId,
          message: messageToSend
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Remove loading message and add real response
        setChatHistory(prev => {
          const withoutLoading = prev.filter(msg => !msg.isLoading);
          
          if (data.hasStructuredResponse) {
            // GPT wants to modify/add a block
            return [...withoutLoading, {
              role: 'assistant',
              content: data.chatResponse,
              timestamp: new Date().toLocaleTimeString(),
              hasAction: true,
              actionType: data.actionType, // 'modify' or 'add'
              blockIndex: data.blockIndex,
              blockData: data.blockData
            }];
          } else {
            // Normal conversational response
            return [...withoutLoading, {
              role: 'assistant',
              content: data.response,
              timestamp: new Date().toLocaleTimeString()
            }];
          }
        });
        
        // console.log(`✅ FREE CHAT: Added response`); // Removed for production
      } else {
        // Remove loading and add error
        setChatHistory(prev => {
          const withoutLoading = prev.filter(msg => !msg.isLoading);
          return [...withoutLoading, {
            role: 'assistant',
            content: '❌ Error al procesar tu mensaje. Inténtalo de nuevo.',
            timestamp: new Date().toLocaleTimeString()
          }];
        });
      }
    } catch (error) {
      console.error('Error:', error);
      // Remove loading and add error
      setChatHistory(prev => {
        const withoutLoading = prev.filter(msg => !msg.isLoading);
        return [...withoutLoading, {
          role: 'assistant',
          content: '❌ Error de conexión. Inténtalo de nuevo.',
          timestamp: new Date().toLocaleTimeString()
        }];
      });
    } finally {
      setIsFreeChatLoading(false);
    }
  };

  const handleApplyBlockAction = (message: any) => {
    if (!statisticData || !message.hasAction) return;
    
    if (message.actionType === 'modify') {
      // Modify existing block
      const newBlocks = [...statisticData.reasoning_blocks];
      newBlocks[message.blockIndex] = message.blockData;
      setStatisticData({
        ...statisticData,
        reasoning_blocks: newBlocks
      });
      
      // console.log(`✅ APPLIED: Modified block ${message.blockIndex}`); // Removed for production
      
      // Add confirmation to chat
      setChatHistory(prev => [...prev, {
        role: 'system',
        content: `✅ Bloque ${message.blockIndex + 1} modificado correctamente`,
        timestamp: new Date().toLocaleTimeString(),
        type: 'confirmation'
      }]);
      
    } else if (message.actionType === 'add') {
      // Add new block after the referenced block
      const newBlocks = [...statisticData.reasoning_blocks];
      newBlocks.splice(message.blockIndex + 1, 0, message.blockData);
      setStatisticData({
        ...statisticData,
        reasoning_blocks: newBlocks
      });
      
      // console.log(`✅ APPLIED: Added new block after ${message.blockIndex}`); // Removed for production
      
      // Add confirmation to chat
      setChatHistory(prev => [...prev, {
        role: 'system',
        content: `✅ Nuevo bloque añadido después del bloque ${message.blockIndex + 1}`,
        timestamp: new Date().toLocaleTimeString(),
        type: 'confirmation'
      }]);
    }
  };

  const handleUpdateCalcsFromBlocks = async () => {
    if (!statisticData) return;
    
    setIsUpdatingCalcs(true);
    try {
      const response = await fetch('/api/assistant/block-calc', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          reasoning_blocks: statisticData.reasoning_blocks,
          question: statisticData.question,
          scope: statisticData.scope
        })
      });

      if (response.ok) {
        const data = await response.json();
        setCalcData(data);
        setCalcSessionId(data.sessionId);
        // console.log(`✅ CALCS: Received calc data with session ${data.sessionId}`); // Removed for production
      } else {
        alert('Error al generar cálculos');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error de conexión');
    } finally {
      setIsUpdatingCalcs(false);
    }
  };

  const handleGenerateArticle = async () => {
    if (!statisticData || !calcData) return;
    
    setIsGeneratingArticle(true);
    try {
      // console.log('📝 ARTICLE: Sending blocks + calcs to editor assistant'); // Removed for production
      
      const payload = {
        preproduction: statisticData, // JSON completo de bloques de razonamiento
        data: calcData // JSON completo de bloques de cálculo
      };
      
      // LOG DETALLADO del payload desde el frontend
      // console.log('📋 FRONTEND PAYLOAD SUMMARY:', {
        // preproduction: {
          // question: payload.preproduction.question?.substring(0, 100) + '...',
          // scope: payload.preproduction.scope,
          // reasoning_blocks_count: payload.preproduction.reasoning_blocks?.length || 0,
          // considerations_count: payload.preproduction.considerations?.length || 0
        // },
        // data: {
          // blocks_count: payload.data.blocks?.length || 0,
          // first_block: payload.data.blocks?.[0] ? {
            // id: payload.data.blocks[0].id,
            // type: payload.data.blocks[0].type,
            // title: payload.data.blocks[0].title?.substring(0, 50) + '...',
            // value: payload.data.blocks[0].value
          // } : null
        // }
      // }); // Removed for production
      
      // console.log('📄 FRONTEND FULL PAYLOAD SIZE:', JSON.stringify(payload).length, 'characters'); // Removed for production
      
      const response = await fetch('/api/assistant/editor', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        const result = await response.json();
        // console.log('✅ ARTICLE: Editor assistant response received'); // Removed for production
        // console.log('📋 ARTICLE: Response structure:', {
          // title: result.title?.substring(0, 50) + '...',
          // meta_title: result.meta_title || 'MISSING',
          // meta_description: result.meta_description || 'MISSING', 
          // category: result.category || 'MISSING',
          // location: result.location || 'MISSING',
          // keywords: result.keywords || 'MISSING',
          // tags: result.tags?.length || 0,
          // highlights: result.highlights?.length || 0
        // }); // Removed for production
        setArticleData(result);
      } else {
        const errBody = await response.json().catch(() => ({}));
        alert(errBody.message || 'Error al generar artículo');
      }
    } catch (error) {
      console.error('❌ ARTICLE: Error calling editor assistant:', error);
      alert('Error de conexión');
    } finally {
      setIsGeneratingArticle(false);
    }
  };

  // Función para generar slug a partir del título
  const generateSlug = (title: string): string => {
    return title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single
      .trim();
  };

  // Función para generar UUID simple
  const generateUUID = (): string => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  // Función para formatear fecha actual
  const getCurrentTimestamp = (): string => {
    return new Date().toISOString().replace('T', ' ').replace('Z', '');
  };

  const handleGenerateResult = () => {
    if (!statisticData || !calcData || !articleData) return;
    
    setIsGeneratingResult(true);
    try {
      // console.log('📊 RESULT: Generating final result data'); // Removed for production
      
      // Generar study_flow desde considerations
      const studyFlow = statisticData.considerations 
        ? statisticData.considerations.map(c => c.description).join(' ')
        : '';
      
      // Generar slug
      const slug = generateSlug(articleData.title);
      const defaultAuthor = authors.find((a) => a.isDefault) || authors[0];
      
      // Crear ResultData
      const result: ResultData = {
        id: generateUUID(),
        location: articleData.location || '',
        language: 'es',
        slug: slug,
        title: articleData.title,
        description: articleData.description,
        study_flow: studyFlow,
        tags: articleData.tags || [],
        author: defaultAuthor?.name || 'Equipo de Statemia',
        authorId: defaultAuthor?.id,
        status: 'draft',
        data: {
          blocks: calcData.blocks,
          charts: [],
          segments: [],
          evolution: ''
        },
        created_at: getCurrentTimestamp(),
        updated_at: getCurrentTimestamp(),
        meta_title: (articleData as any).meta_title || (articleData as any).metaTitle || '',
        meta_description: (articleData as any).meta_description || (articleData as any).metaDescription || '',
        keywords: (articleData as any).keywords || '',
        og_image: '',
        canonical_url: `/statistics/${slug}`,
        lead: articleData.lead,
        image_id: '',
        related_ad_products: [],
        type: 'statistic',
        related_links: [],
        og_image_alt: '',
        category: articleData.category || '',
        highlights: articleData.highlights || [],
        conclusion: articleData.conclusion,
        featured: false,
        radar: ''
      };
      
      setResultData(result);
      // console.log('✅ RESULT: Generated result data with meta fields:', {
        // title: result.title.substring(0, 50) + '...',
        // meta_title: result.meta_title || 'EMPTY',
        // meta_description: result.meta_description || 'EMPTY',
        // category: result.category || 'EMPTY',
        // location: result.location || 'EMPTY',
        // keywords: result.keywords || 'EMPTY'
      // }); // Removed for production
      
    } catch (error) {
      console.error('❌ RESULT: Error generating result:', error);
      alert('Error al generar resultado');
    } finally {
      setIsGeneratingResult(false);
    }
  };

  const handleSaveStatistic = async () => {
    if (!resultData) return;
    
    setIsSavingStatistic(true);
    try {
      // console.log('💾 SAVE: Saving statistic to database'); // Removed for production
      
      // Preparar datos para la API (mapear a formato de DB)
      const statisticPayload = {
        title: resultData.title,
        description: resultData.description,
        studyFlow: resultData.study_flow,
        location: resultData.location,
        language: resultData.language,
        slug: resultData.slug,
        tags: resultData.tags,
        author: resultData.author,
        authorId: resultData.authorId,
        status: resultData.status,
        type: resultData.type,
        data: resultData.data,
        lead: resultData.lead,
        metaTitle: resultData.meta_title,
        metaDescription: resultData.meta_description,
        keywords: resultData.keywords,
        ogImage: resultData.og_image,
        ogImageAlt: resultData.og_image_alt,
        canonicalUrl: resultData.canonical_url,
        relatedLinks: resultData.related_links,
        relatedAdProducts: resultData.related_ad_products,
        category: resultData.category,
        highlights: resultData.highlights,
        conclusion: resultData.conclusion,
        featured: resultData.featured,
        radar: resultData.radar
      };
      
      // console.log('💾 SAVE: Payload prepared:', {
        // title: statisticPayload.title.substring(0, 50) + '...',
        // slug: statisticPayload.slug,
        // status: statisticPayload.status,
        // dataBlocksCount: statisticPayload.data.blocks.length
      // }); // Removed for production
      
      const response = await fetch('/api/statistics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(statisticPayload)
      });
      
      if (response.ok) {
        const savedStatistic = await response.json();
        // console.log('✅ SAVE: Statistic saved successfully:', savedStatistic.id); // Removed for production
        
        alert(`¡Estadística guardada exitosamente!\nID: ${savedStatistic.id}\nSlug: ${savedStatistic.slug}`);
        
        // Cerrar modal y limpiar estados
        onClose();
        
        // Limpiar todos los estados
        setActiveTab('question');
        setQuestion('');
        setStatisticData(null);
        setCalcData(null);
        setArticleData(null);
        setResultData(null);
        setSessionId(null);
        setCalcSessionId(null);
        setChatHistory([]);
        setCalcChatHistory([]);
        
      } else {
        const errorData = await response.json();
        console.error('❌ SAVE: Error saving statistic:', errorData);
        alert(`Error al guardar: ${errorData.message || 'Error desconocido'}`);
      }
      
    } catch (error) {
      console.error('❌ SAVE: Network error:', error);
      alert('Error de conexión al guardar la estadística');
    } finally {
      setIsSavingStatistic(false);
    }
  };

  const handleCalcChatMessage = async () => {
    if (!calcChatMessage.trim() || !calcSessionId) return;
    
    // Add user message to calc chat immediately
    const userMessage = {
      role: 'user',
      content: calcChatMessage.trim(),
      timestamp: new Date().toLocaleTimeString()
    };
    
    setCalcChatHistory(prev => [...prev, userMessage]);
    
    // Clear input and set loading
    const messageToSend = calcChatMessage.trim();
    setCalcChatMessage('');
    setIsCalcChatLoading(true);
    
    // Add loading message
    const loadingMessage = {
      role: 'assistant',
      content: '⏳ Analizando cálculos...',
      timestamp: new Date().toLocaleTimeString(),
      isLoading: true
    };
    
    setCalcChatHistory(prev => [...prev, loadingMessage]);
    
    try {
      const response = await fetch('/api/gpt/calc-chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          sessionId: calcSessionId,
          message: messageToSend
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Remove loading and add response
        setCalcChatHistory(prev => {
          const withoutLoading = prev.filter(msg => !msg.isLoading);
          return [...withoutLoading, {
            role: 'assistant',
            content: data.response,
            timestamp: new Date().toLocaleTimeString()
          }];
        });
      } else {
        // Remove loading and add error
        setCalcChatHistory(prev => {
          const withoutLoading = prev.filter(msg => !msg.isLoading);
          return [...withoutLoading, {
            role: 'assistant',
            content: '❌ Error al procesar tu pregunta sobre cálculos.',
            timestamp: new Date().toLocaleTimeString()
          }];
        });
      }
    } catch (error) {
      console.error('Error:', error);
      setCalcChatHistory(prev => {
        const withoutLoading = prev.filter(msg => !msg.isLoading);
        return [...withoutLoading, {
          role: 'assistant',
          content: '❌ Error de conexión.',
          timestamp: new Date().toLocaleTimeString()
        }];
      });
    } finally {
      setIsCalcChatLoading(false);
    }
  };

  const handleCalcGptQuestion = async (blockIndex: number) => {
    const currentQuestion = calcGptQuestions[blockIndex]?.trim();
    if (!currentQuestion || !calcData) return;
    
    // Get current calc block content
    const currentBlock = calcData.blocks[blockIndex];
    
    // Add user message to calc chat immediately with full block context
    const userMessage = {
      role: 'user',
      content: `Sobre el bloque de cálculo ${currentBlock.id}: ${currentQuestion}

🧮 **Contenido actual del bloque:**
**Título:** ${currentBlock.title}
**Tipo:** ${currentBlock.type}
**Valor:** ${currentBlock.value} ${currentBlock.unit}
**Fórmula:** ${currentBlock.formula || 'N/A'}
**Descripción:** ${currentBlock.description}
**Fuente:** ${currentBlock.source || 'N/A'}`,
      timestamp: new Date().toLocaleTimeString(),
      type: 'calc_block_question',
      blockIndex
    };
    
    setCalcChatHistory(prev => [...prev, userMessage]);
    
    // Set loading for this specific calc block
    setIsCalcGptLoading(prev => ({ ...prev, [blockIndex]: true }));
    
    // Add loading message to calc chat
    const loadingMessage = {
      role: 'assistant',
      content: '⏳ Analizando el bloque de cálculo...',
      timestamp: new Date().toLocaleTimeString(),
      isLoading: true
    };
    
    setCalcChatHistory(prev => [...prev, loadingMessage]);
    
    try {
      const response = await fetch('/api/gpt/calc-block-question', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          sessionId: calcSessionId,
          blockIndex,
          question: currentQuestion
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Remove loading message and add real response
        setCalcChatHistory(prev => {
          const withoutLoading = prev.filter(msg => !msg.isLoading);
          return [...withoutLoading, {
            role: 'assistant',
            content: data.response || data.chatResponse,
            timestamp: new Date().toLocaleTimeString()
          }];
        });
        
        // Clear the question for this specific calc block
        setCalcGptQuestions(prev => ({ ...prev, [blockIndex]: '' }));
        
        // console.log(`✅ CALC CHAT: Added GPT response for calc block ${blockIndex}`); // Removed for production
      } else {
        // Remove loading and add error
        setCalcChatHistory(prev => {
          const withoutLoading = prev.filter(msg => !msg.isLoading);
          return [...withoutLoading, {
            role: 'assistant',
            content: '❌ Error al procesar tu pregunta sobre el cálculo.',
            timestamp: new Date().toLocaleTimeString()
          }];
        });
      }
    } catch (error) {
      console.error('Error:', error);
      setCalcChatHistory(prev => {
        const withoutLoading = prev.filter(msg => !msg.isLoading);
        return [...withoutLoading, {
          role: 'assistant',
          content: '❌ Error de conexión.',
          timestamp: new Date().toLocaleTimeString()
        }];
      });
    } finally {
      // Remove loading for this specific calc block
      setIsCalcGptLoading(prev => ({ ...prev, [blockIndex]: false }));
    }
  };

  const handleEditCalcBlock = (index: number, updatedBlock: CalcBlock) => {
    if (!calcData) return;
    const newBlocks = [...calcData.blocks];
    newBlocks[index] = updatedBlock;
    setCalcData({
      ...calcData,
      blocks: newBlocks
    });
    setEditingCalcBlockIndex(null);
  };

  const handleDeleteCalcBlock = (index: number) => {
    if (!calcData) return;
    const newBlocks = calcData.blocks.filter((_, i) => i !== index);
    
    // Resequence order values for remaining blocks
    const resequencedBlocks = newBlocks.map((block, i) => ({
      ...block,
      order: i + 1
    }));
    
    setCalcData({
      ...calcData,
      blocks: resequencedBlocks
    });
    // console.log(`✅ CALC: Deleted block and resequenced orders`); // Removed for production
  };

  const handleAddReasoningBlock = (afterIndex: number) => {
    if (!statisticData) return;
    
    const newBlock: ReasoningBlock = {
      question: "Nueva pregunta de razonamiento",
      description: "Descripción del nuevo bloque de razonamiento",
      sources: ["https://example.com"]
    };
    
    const newBlocks = [...statisticData.reasoning_blocks];
    newBlocks.splice(afterIndex + 1, 0, newBlock);
    
    setStatisticData({
      ...statisticData,
      reasoning_blocks: newBlocks
    });
    
    // console.log(`✅ REASONING: Added new block after index ${afterIndex}`); // Removed for production
  };

  const handleAddCalcBlock = (afterIndex: number) => {
    if (!calcData) return;
    
    const newOrder = afterIndex + 2; // Next order after the block we're inserting after
    
    const newBlock: CalcBlock = {
      id: `B${newOrder}`,
      type: 'calculated',
      unit: 'unidad',
      order: newOrder,
      title: 'Nuevo bloque de cálculo',
      value: 0,
      formula: '',
      image_icon: '',
      description: 'Descripción del nuevo bloque de cálculo',
      source: '',
      source_url: ''
    };
    
    const newBlocks = [...calcData.blocks];
    newBlocks.splice(afterIndex + 1, 0, newBlock);
    
    // Resequence order values for blocks after the inserted one
    const resequencedBlocks = newBlocks.map((block, i) => ({
      ...block,
      order: i + 1,
      id: `B${i + 1}` // Also update IDs to match order
    }));
    
    setCalcData({
      ...calcData,
      blocks: resequencedBlocks
    });
    
    // console.log(`✅ CALC: Added new block after index ${afterIndex} and resequenced`); // Removed for production
  };

  const loadChatHistory = async () => {
    if (!sessionId) return;
    
    try {
      const response = await fetch(`/api/gpt/chat/${sessionId}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setChatHistory(data.messages);
        // console.log(`✅ CHAT: Loaded ${data.messages.length} messages`); // Removed for production
      }
    } catch (error) {
      console.error('Error loading chat history:', error);
    }
  };

  const handleClose = () => {
    setQuestion('');
    setStatisticData(null);
    setSessionId(null);
    setChatHistory([]);
    setEditingBlockIndex(null);
    setGptQuestions({});
    setIsGptLoading({});
    setCalcData(null);
    setCalcSessionId(null);
    setCalcChatHistory([]);
    setCalcChatMessage('');
    setJsonView('reasoning');
    setActiveTab('question');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <Brain className="h-6 w-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">Nueva Estadística (NEW)</h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('question')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'question'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <MessageSquare className="inline w-4 h-4 mr-2" />
            Pregunta
          </button>
          <button
            onClick={() => setActiveTab('blocks')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'blocks'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            disabled={!statisticData}
          >
            <Edit className="inline w-4 h-4 mr-2" />
            Bloques
          </button>
          <button
            onClick={() => setActiveTab('calcs')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'calcs'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            disabled={!statisticData}
          >
            <BarChart3 className="inline w-4 h-4 mr-2" />
            Calcs
          </button>
          <button
            onClick={() => setActiveTab('article')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'article'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            disabled={!statisticData || !calcData}
          >
            <FileEdit className="inline w-4 h-4 mr-2" />
            Article
          </button>
          <button
            onClick={() => setActiveTab('result')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'result'
                ? 'text-green-600 border-b-2 border-green-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            disabled={!statisticData || !calcData || !articleData}
          >
            <FileText className="inline w-4 h-4 mr-2" />
            Result
          </button>
          <button
            onClick={() => setActiveTab('json')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'json'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            disabled={!statisticData}
          >
            <FileText className="inline w-4 h-4 mr-2" />
            JSON
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          {/* Question Tab */}
          {activeTab === 'question' && (
            <div className="p-6">
              <div className="max-w-2xl mx-auto">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Plantea tu pregunta estadística:
                </label>
                <textarea
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder="Ejemplo: ¿Cuántas uvas se comen en Nochevieja en España?"
                  className="w-full h-32 p-4 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={isLoading}
                />
                <button
                  onClick={handleSubmitQuestion}
                  disabled={!question.trim() || isLoading}
                  className="mt-4 w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      Procesando...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Enviar Pregunta
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Blocks Tab with Chat Column */}
          {activeTab === 'blocks' && statisticData && (
            <div className="flex h-full">
              {/* Left Column - Blocks */}
              <div className="flex-1 p-6 overflow-auto border-r border-gray-200">
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {statisticData.question}
                  </h3>
                  <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                    <strong>Alcance:</strong> {statisticData.scope.region} | {statisticData.scope.event_window} | {statisticData.scope.timezone}
                  </div>
                </div>

                {/* Reasoning Blocks */}
                <div className="space-y-2 mb-8">
                  <h4 className="text-md font-semibold text-gray-800">Bloques de Razonamiento:</h4>
                  {statisticData.reasoning_blocks.map((block, index) => (
                    <div key={index}>
                      <div className="border border-gray-200 rounded-lg p-4 bg-white">
                        {editingBlockIndex === index ? (
                          // Edit Mode
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Pregunta:</label>
                              <input
                                type="text"
                                defaultValue={block.question}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500"
                                id={`edit-question-${index}`}
                              />
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Descripción:</label>
                              <textarea
                                defaultValue={block.description}
                                rows={4}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500"
                                id={`edit-description-${index}`}
                              />
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Fuentes (una por línea):</label>
                              <textarea
                                defaultValue={block.sources.join('\n')}
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500"
                                id={`edit-sources-${index}`}
                              />
                            </div>
                            
                            <div className="flex gap-2">
                              <button
                                onClick={() => {
                                  const question = (document.getElementById(`edit-question-${index}`) as HTMLInputElement).value;
                                  const description = (document.getElementById(`edit-description-${index}`) as HTMLTextAreaElement).value;
                                  const sources = (document.getElementById(`edit-sources-${index}`) as HTMLTextAreaElement).value
                                    .split('\n')
                                    .filter(s => s.trim())
                                    .map(s => s.trim());
                                  
                                  handleEditBlock(index, { question, description, sources });
                                }}
                                className="bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700 flex items-center gap-2"
                              >
                                <Eye size={16} />
                                Guardar
                              </button>
                              <button
                                onClick={() => setEditingBlockIndex(null)}
                                className="bg-gray-500 text-white px-4 py-2 rounded text-sm hover:bg-gray-600"
                              >
                                Cancelar
                              </button>
                            </div>
                          </div>
                        ) : (
                          // View Mode
                          <>
                            <div className="flex items-start justify-between mb-3">
                              <h5 className="font-medium text-gray-900 flex-1">{block.question}</h5>
                              <div className="flex gap-2 ml-4">
                                <button
                                  onClick={() => setEditingBlockIndex(index)}
                                  className="text-blue-600 hover:text-blue-800"
                                >
                                  <Edit size={16} />
                                </button>
                                <button
                                  onClick={() => handleDeleteBlock(index)}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </div>
                            
                            <p className="text-gray-700 text-sm mb-3">{block.description}</p>
                            
                            <div className="text-xs text-gray-500">
                              <strong>Fuentes:</strong>
                              <ul className="mt-1 space-y-1">
                                {block.sources.map((source, sourceIndex) => (
                                  <li key={sourceIndex}>
                                    <a href={source} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                      {source}
                                    </a>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </>
                        )}

                        {/* GPT Question for this specific block */}
                        <div className="mt-4 pt-4 border-t border-gray-100">
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={gptQuestions[index] || ''}
                              onChange={(e) => setGptQuestions(prev => ({ ...prev, [index]: e.target.value }))}
                              placeholder={`Pregunta a GPT sobre el bloque ${index + 1}...`}
                              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                              disabled={isGptLoading[index]}
                            />
                            <button
                              onClick={() => handleGptQuestion(index)}
                              disabled={!gptQuestions[index]?.trim() || isGptLoading[index]}
                              className="bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isGptLoading[index] ? <Loader className="w-4 h-4 animate-spin" /> : <Send size={16} />}
                            </button>
                          </div>
                          {isGptLoading[index] && (
                            <div className="mt-2 text-xs text-gray-500 flex items-center gap-1">
                              <Loader className="w-3 h-3 animate-spin" />
                              Consultando a GPT sobre este bloque...
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Add Block Button */}
                      <div className="flex justify-center py-2">
                        <button
                          onClick={() => handleAddReasoningBlock(index)}
                          className="w-8 h-8 bg-green-100 hover:bg-green-200 text-green-600 rounded-full flex items-center justify-center text-lg font-bold transition-colors"
                          title={`Añadir bloque después del ${index + 1}`}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Considerations */}
                <div className="space-y-4">
                  <h4 className="text-md font-semibold text-gray-800">Consideraciones:</h4>
                  {statisticData.considerations.map((consideration, index) => (
                    <div key={index} className="border border-orange-200 rounded-lg p-4 bg-orange-50">
                      <p className="text-gray-700 text-sm mb-2">{consideration.description}</p>
                      <div className="text-xs text-gray-500">
                        <strong>Fuente:</strong>
                        <a href={consideration.source} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline ml-1">
                          {consideration.source}
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right Column - Chat */}
              <ChatColumn
                title="Chat"
                icon={<MessageSquare className="w-4 h-4 text-green-600" />}
                chatHistory={chatHistory}
                chatMessage={freeChatMessage}
                setChatMessage={setFreeChatMessage}
                onSendMessage={handleFreeChatMessage}
                isLoading={isFreeChatLoading}
                sessionId={sessionId}
                placeholder="Escribe libremente sobre el estudio..."
              />
            </div>
          )}

          {/* Calcs Tab */}
          {activeTab === 'calcs' && (
            <div className="flex h-full">
              {/* Left Column - Calc Blocks */}
              <div className="flex-1 p-6 overflow-auto border-r border-gray-200">
                {/* Header with Update Button */}
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Bloques de Cálculo</h3>
                    <p className="text-sm text-gray-600">Procesos matemáticos derivados del análisis</p>
                  </div>
                  <button
                    onClick={handleUpdateCalcsFromBlocks}
                    disabled={isUpdatingCalcs || !statisticData}
                    className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {isUpdatingCalcs ? (
                      <>
                        <Loader className="w-4 h-4 animate-spin" />
                        Generando...
                      </>
                    ) : (
                      <>
                        <BarChart3 className="w-4 h-4" />
                        Actualizar desde Bloques
                      </>
                    )}
                  </button>
                </div>

                {/* Calc Blocks */}
                {calcData ? (
                  <div className="space-y-2">
                    {calcData.blocks.map((block, index) => (
                      <div key={block.id}>
                        <div className="border border-gray-200 rounded-lg p-4 bg-white">
                          {editingCalcBlockIndex === index ? (
                            // Edit Mode for Calc Block
                            <div className="space-y-4">
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">ID:</label>
                                  <input
                                    type="text"
                                    defaultValue={block.id}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500"
                                    id={`edit-calc-id-${index}`}
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo:</label>
                                  <select
                                    defaultValue={block.type}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500"
                                    id={`edit-calc-type-${index}`}
                                  >
                                    <option value="extracted">Extracted</option>
                                    <option value="calculated">Calculated</option>
                                    <option value="estimated">Estimated</option>
                                    <option value="assumption">Assumption</option>
                                  </select>
                                </div>
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Título:</label>
                                <input
                                  type="text"
                                  defaultValue={block.title}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500"
                                  id={`edit-calc-title-${index}`}
                                />
                              </div>

                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Valor:</label>
                                  <input
                                    type="text"
                                    defaultValue={block.value.toString()}
                                    placeholder="Ej: 123456 o '333-500 millones'"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500"
                                    id={`edit-calc-value-${index}`}
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Unidad:</label>
                                  <input
                                    type="text"
                                    defaultValue={block.unit}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500"
                                    id={`edit-calc-unit-${index}`}
                                  />
                                </div>
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Fórmula:</label>
                                <input
                                  type="text"
                                  defaultValue={block.formula}
                                  placeholder="Ej: B1 * B2 / 1000"
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500"
                                  id={`edit-calc-formula-${index}`}
                                />
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Descripción:</label>
                                <textarea
                                  defaultValue={block.description}
                                  rows={3}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500"
                                  id={`edit-calc-description-${index}`}
                                />
                              </div>

                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Fuente:</label>
                                  <input
                                    type="text"
                                    defaultValue={block.source}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500"
                                    id={`edit-calc-source-${index}`}
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">URL Fuente:</label>
                                  <input
                                    type="url"
                                    defaultValue={block.source_url}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500"
                                    id={`edit-calc-source-url-${index}`}
                                  />
                                </div>
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Icono (URL):</label>
                                <input
                                  type="url"
                                  defaultValue={block.image_icon}
                                  placeholder="https://example.com/icon.png"
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500"
                                  id={`edit-calc-icon-${index}`}
                                />
                              </div>
                              
                              <div className="flex gap-2">
                                <button
                                  onClick={() => {
                                    const id = (document.getElementById(`edit-calc-id-${index}`) as HTMLInputElement).value;
                                    const type = (document.getElementById(`edit-calc-type-${index}`) as HTMLSelectElement).value as CalcBlock['type'];
                                    const title = (document.getElementById(`edit-calc-title-${index}`) as HTMLInputElement).value;
                                    const valueInput = (document.getElementById(`edit-calc-value-${index}`) as HTMLInputElement).value;
                                    const value = isNaN(Number(valueInput)) ? valueInput : Number(valueInput);
                                    const unit = (document.getElementById(`edit-calc-unit-${index}`) as HTMLInputElement).value;
                                    const formula = (document.getElementById(`edit-calc-formula-${index}`) as HTMLInputElement).value;
                                    const description = (document.getElementById(`edit-calc-description-${index}`) as HTMLTextAreaElement).value;
                                    const source = (document.getElementById(`edit-calc-source-${index}`) as HTMLInputElement).value;
                                    const source_url = (document.getElementById(`edit-calc-source-url-${index}`) as HTMLInputElement).value;
                                    const image_icon = (document.getElementById(`edit-calc-icon-${index}`) as HTMLInputElement).value;
                                    
                                    handleEditCalcBlock(index, {
                                      id, type, title, value, unit, formula, description, source, source_url, image_icon,
                                      order: block.order // Keep original order
                                    });
                                  }}
                                  className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 flex items-center gap-2"
                                >
                                  <Eye size={16} />
                                  Guardar
                                </button>
                                <button
                                  onClick={() => setEditingCalcBlockIndex(null)}
                                  className="bg-gray-500 text-white px-4 py-2 rounded text-sm hover:bg-gray-600"
                                >
                                  Cancelar
                                </button>
                              </div>
                            </div>
                          ) : (
                            // View Mode for Calc Block
                            <>
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center gap-3">
                                  {block.image_icon && (
                                    <img src={block.image_icon} alt={block.title} className="w-8 h-8" />
                                  )}
                                  <div>
                                    <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                                      block.type === 'extracted' ? 'bg-green-100 text-green-800' :
                                      block.type === 'calculated' ? 'bg-blue-100 text-blue-800' :
                                      block.type === 'estimated' ? 'bg-yellow-100 text-yellow-800' :
                                      'bg-gray-100 text-gray-800'
                                    }`}>
                                      {block.type.toUpperCase()}
                                    </span>
                                    <h5 className="font-medium text-gray-900 mt-1">{block.title}</h5>
                                  </div>
                                </div>
                                <div className="flex gap-2">
                                  <button 
                                    onClick={() => setEditingCalcBlockIndex(index)}
                                    className="text-blue-600 hover:text-blue-800"
                                  >
                                    <Edit size={16} />
                                  </button>
                                  <button 
                                    onClick={() => handleDeleteCalcBlock(index)}
                                    className="text-red-600 hover:text-red-800"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              </div>
                              
                              <div className="grid grid-cols-2 gap-4 mb-3 text-sm">
                                <div>
                                  <span className="font-medium text-gray-700">Valor:</span>
                                  <span className="ml-2 text-2xl font-bold text-blue-600">
                                    {block.value} {block.unit}
                                  </span>
                                </div>
                                <div>
                                  <span className="font-medium text-gray-700">Orden:</span>
                                  <span className="ml-2">{block.order}</span>
                                </div>
                              </div>
                              
                              {block.formula && (
                                <div className="mb-3">
                                  <span className="font-medium text-gray-700 text-sm">Fórmula:</span>
                                  <code className="ml-2 px-2 py-1 bg-gray-100 rounded text-sm">{block.formula}</code>
                                </div>
                              )}
                              
                              <p className="text-gray-700 text-sm mb-3">{block.description}</p>
                              
                              {block.source && (
                                <div className="text-xs text-gray-500 mb-4">
                                  <strong>Fuente:</strong>
                                  <a href={block.source_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline ml-1">
                                    {block.source}
                                  </a>
                                </div>
                              )}
                            </>
                          )}

                          {/* GPT Question for this specific calc block */}
                          <div className="mt-4 pt-4 border-t border-gray-100">
                            <div className="flex gap-2">
                              <input
                                type="text"
                                value={calcGptQuestions[index] || ''}
                                onChange={(e) => setCalcGptQuestions(prev => ({ ...prev, [index]: e.target.value }))}
                                placeholder={`Pregunta a GPT sobre el cálculo ${block.id}...`}
                                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                                disabled={isCalcGptLoading[index]}
                              />
                              <button
                                onClick={() => handleCalcGptQuestion(index)}
                                disabled={!calcGptQuestions[index]?.trim() || isCalcGptLoading[index]}
                                className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {isCalcGptLoading[index] ? <Loader className="w-4 h-4 animate-spin" /> : <Send size={16} />}
                              </button>
                            </div>
                            {isCalcGptLoading[index] && (
                              <div className="mt-2 text-xs text-gray-500 flex items-center gap-1">
                                <Loader className="w-3 h-3 animate-spin" />
                                Consultando a GPT sobre este cálculo...
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Add Calc Block Button */}
                        <div className="flex justify-center py-2">
                          <button
                            onClick={() => handleAddCalcBlock(index)}
                            className="w-8 h-8 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-full flex items-center justify-center text-lg font-bold transition-colors"
                            title={`Añadir bloque de cálculo después del ${block.id}`}
                          >
                            +
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-gray-500 py-12">
                    <BarChart3 className="mx-auto h-12 w-12 text-gray-300 mb-4" />
                    <p className="text-lg mb-2">No hay cálculos generados</p>
                    <p className="text-sm">Haz click en "Actualizar desde Bloques" para generar los cálculos matemáticos</p>
                  </div>
                )}
              </div>

              {/* Right Column - Chat for Calcs */}
              <ChatColumn
                title="Chat Cálculos"
                icon={<BarChart3 className="w-4 h-4 text-blue-600" />}
                chatHistory={calcChatHistory}
                chatMessage={calcChatMessage}
                setChatMessage={setCalcChatMessage}
                onSendMessage={handleCalcChatMessage}
                isLoading={isCalcChatLoading}
                sessionId={calcSessionId}
                placeholder="Pregunta sobre los cálculos..."
                bgColor="bg-blue-50"
              />
            </div>
          )}

          {/* Result Tab */}
          {activeTab === 'result' && (
            <div className="p-6">
              {/* Header with Generate Button */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Resultado Final</h3>
                  <p className="text-sm text-gray-600">Formulario completo para guardar en la base de datos</p>
                </div>
                <button
                  onClick={handleGenerateResult}
                  disabled={isGeneratingResult || !statisticData || !calcData || !articleData}
                  className="bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {isGeneratingResult ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      Generando...
                    </>
                  ) : (
                    <>
                      <FileText className="w-4 h-4" />
                      Generate
                    </>
                  )}
                </button>
              </div>

              {/* Form */}
              {resultData ? (
                <div className="space-y-4">
                  {/* Basic Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">ID:</label>
                      <input
                        type="text"
                        value={resultData.id}
                        onChange={(e) => setResultData({...resultData, id: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-green-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Slug:</label>
                      <input
                        type="text"
                        value={resultData.slug}
                        onChange={(e) => setResultData({...resultData, slug: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-green-500 text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title:</label>
                    <input
                      type="text"
                      value={resultData.title}
                      onChange={(e) => setResultData({...resultData, title: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-green-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description:</label>
                    <textarea
                      value={resultData.description}
                      onChange={(e) => setResultData({...resultData, description: e.target.value})}
                      rows={8}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-green-500 text-sm font-mono"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Lead:</label>
                      <textarea
                        value={resultData.lead}
                        onChange={(e) => setResultData({...resultData, lead: e.target.value})}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-green-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Category:</label>
                      <input
                        type="text"
                        value={resultData.category}
                        onChange={(e) => setResultData({...resultData, category: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-green-500 text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Study Flow:</label>
                    <textarea
                      value={resultData.study_flow}
                      onChange={(e) => setResultData({...resultData, study_flow: e.target.value})}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-green-500 text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tags (separados por comas):</label>
                    <input
                      type="text"
                      value={resultData.tags.join(', ')}
                      onChange={(e) => setResultData({...resultData, tags: e.target.value.split(',').map(t => t.trim()).filter(t => t)})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-green-500 text-sm"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Autor:</label>
                      <select
                        value={resultData.authorId || ''}
                        onChange={(e) => {
                          const selected = authors.find((a) => a.id === e.target.value);
                          setResultData({
                            ...resultData,
                            authorId: selected?.id,
                            author: selected?.name || resultData.author,
                          });
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-green-500 text-sm"
                      >
                        {authors.length === 0 && (
                          <option value="">Equipo de Statemia</option>
                        )}
                        {authors.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.name}{a.isDefault ? ' (por defecto)' : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Status:</label>
                      <select
                        value={resultData.status}
                        onChange={(e) => setResultData({...resultData, status: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-green-500 text-sm"
                      >
                        <option value="draft">Draft</option>
                        <option value="published">Published</option>
                      </select>
                    </div>
                  </div>

                  {/* Meta Fields */}
                  <div className="border-t pt-4">
                    <h4 className="text-md font-semibold text-gray-800 mb-3">Meta Information:</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Meta Title:</label>
                        <input
                          type="text"
                          value={resultData.meta_title}
                          onChange={(e) => setResultData({...resultData, meta_title: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-green-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Keywords:</label>
                        <input
                          type="text"
                          value={resultData.keywords}
                          onChange={(e) => setResultData({...resultData, keywords: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-green-500 text-sm"
                        />
                      </div>
                    </div>
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Meta Description:</label>
                      <textarea
                        value={resultData.meta_description}
                        onChange={(e) => setResultData({...resultData, meta_description: e.target.value})}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-green-500 text-sm"
                      />
                    </div>
                  </div>

                  {/* Highlights */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Highlights:</label>
                    {resultData.highlights.map((highlight, index) => (
                      <div key={index} className="mb-2 flex gap-2">
                        <textarea
                          value={highlight}
                          onChange={(e) => {
                            const newHighlights = [...resultData.highlights];
                            newHighlights[index] = e.target.value;
                            setResultData({...resultData, highlights: newHighlights});
                          }}
                          rows={2}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-green-500 text-sm"
                        />
                        <button
                          onClick={() => {
                            const newHighlights = resultData.highlights.filter((_, i) => i !== index);
                            setResultData({...resultData, highlights: newHighlights});
                          }}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => setResultData({...resultData, highlights: [...resultData.highlights, '']})}
                      className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 flex items-center gap-1"
                    >
                      <Plus size={16} />
                      Añadir Highlight
                    </button>
                  </div>

                  {/* Conclusion */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Conclusion:</label>
                    <textarea
                      value={resultData.conclusion}
                      onChange={(e) => setResultData({...resultData, conclusion: e.target.value})}
                      rows={6}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-green-500 text-sm font-mono"
                    />
                  </div>

                  {/* Save Button */}
                  <div className="border-t pt-6">
                    <button
                      onClick={handleSaveStatistic}
                      disabled={isSavingStatistic}
                      className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg text-lg font-semibold hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {isSavingStatistic ? (
                        <>
                          <Loader className="w-5 h-5 animate-spin" />
                          Guardando...
                        </>
                      ) : (
                        <>
                          <Eye className="w-5 h-5" />
                          Guardar Estadística
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">
                    {!articleData ? 'Primero genera el artículo' : 'Haz clic en "Generate" para crear el formulario final'}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Article Tab */}
          {activeTab === 'article' && (
            <div className="flex h-full">
              {/* Main Article Content */}
              <div className="flex-1 p-6 overflow-auto">
                {/* Header with Generate Button */}
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Artículo Generado</h3>
                    <p className="text-sm text-gray-600">Contenido editorial basado en el análisis</p>
                  </div>
                  <button
                    onClick={handleGenerateArticle}
                    disabled={isGeneratingArticle || !statisticData || !calcData}
                    className="bg-purple-600 text-white px-4 py-2 rounded text-sm hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {isGeneratingArticle ? (
                      <>
                        <Loader className="w-4 h-4 animate-spin" />
                        Generando...
                      </>
                    ) : (
                      <>
                        <FileEdit className="w-4 h-4" />
                        Generar Artículo
                      </>
                    )}
                  </button>
                </div>

                {/* Article Content */}
                {articleData ? (
                  <div className="space-y-6">
                    {/* Title */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Título:</label>
                      <input
                        type="text"
                        value={articleData.title}
                        onChange={(e) => setArticleData({...articleData, title: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-purple-500 text-lg font-semibold"
                      />
                    </div>

                    {/* Lead */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Lead:</label>
                      <textarea
                        value={articleData.lead}
                        onChange={(e) => setArticleData({...articleData, lead: e.target.value})}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-purple-500"
                      />
                    </div>

                    {/* Tags */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Tags (separados por comas):</label>
                      <input
                        type="text"
                        value={articleData.tags.join(', ')}
                        onChange={(e) => setArticleData({...articleData, tags: e.target.value.split(',').map(t => t.trim()).filter(t => t)})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-purple-500"
                        placeholder="energía, cargadores móviles, España"
                      />
                    </div>

                    {/* Description */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Descripción (Markdown permitido):</label>
                      <textarea
                        value={articleData.description}
                        onChange={(e) => setArticleData({...articleData, description: e.target.value})}
                        rows={20}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-purple-500 font-mono text-sm"
                        placeholder="Contenido del artículo con markdown..."
                      />
                    </div>

                    {/* Highlights */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Highlights:</label>
                      {articleData.highlights.map((highlight, index) => (
                        <div key={index} className="mb-2 flex gap-2">
                          <textarea
                            value={highlight}
                            onChange={(e) => {
                              const newHighlights = [...articleData.highlights];
                              newHighlights[index] = e.target.value;
                              setArticleData({...articleData, highlights: newHighlights});
                            }}
                            rows={2}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-purple-500"
                          />
                          <button
                            onClick={() => {
                              const newHighlights = articleData.highlights.filter((_, i) => i !== index);
                              setArticleData({...articleData, highlights: newHighlights});
                            }}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={() => setArticleData({...articleData, highlights: [...articleData.highlights, '']})}
                        className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 flex items-center gap-1"
                      >
                        <Plus size={16} />
                        Añadir Highlight
                      </button>
                    </div>

                    {/* Conclusion */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Conclusión (Markdown permitido):</label>
                      <textarea
                        value={articleData.conclusion}
                        onChange={(e) => setArticleData({...articleData, conclusion: e.target.value})}
                        rows={10}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-purple-500 font-mono text-sm"
                        placeholder="Conclusión del artículo con markdown..."
                      />
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <FileEdit className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">
                      {!calcData ? 'Primero genera los bloques de cálculo' : 'Haz clic en "Generar Artículo" para crear el contenido editorial'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* JSON Tab */}
          {activeTab === 'json' && (
            <div className="p-6">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Vista JSON</h3>
                <p className="text-sm text-gray-600">
                  Estructura de datos actual del análisis
                </p>
              </div>
              
              {/* JSON Selector */}
              <div className="flex gap-4 mb-4">
                <button
                  onClick={() => setJsonView('reasoning')}
                  className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                    jsonView === 'reasoning'
                      ? 'bg-green-600 text-white'
                      : 'bg-green-100 text-green-800 hover:bg-green-200'
                  }`}
                >
                  📋 JSON de Bloques de Razonamiento
                </button>
                {calcData && (
                  <button
                    onClick={() => setJsonView('calc')}
                    className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                      jsonView === 'calc'
                        ? 'bg-blue-600 text-white'
                        : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                    }`}
                  >
                    🧮 JSON de Bloques de Cálculo
                  </button>
                )}
                {articleData && (
                  <button
                    onClick={() => setJsonView('article')}
                    className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                      jsonView === 'article'
                        ? 'bg-purple-600 text-white'
                        : 'bg-purple-100 text-purple-800 hover:bg-purple-200'
                    }`}
                  >
                    📝 JSON del Artículo
                  </button>
                )}
              </div>
              
              <div className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-auto">
                <div className="text-xs text-gray-400 mb-2">
                  Mostrando: {
                    jsonView === 'reasoning' ? 'Bloques de Razonamiento' : 
                    jsonView === 'calc' ? 'Bloques de Cálculo' : 
                    'Datos del Artículo'
                  }
                </div>
                <pre className="text-sm">
                  {jsonView === 'reasoning' 
                    ? JSON.stringify(statisticData, null, 2)
                    : jsonView === 'calc'
                    ? JSON.stringify(calcData, null, 2)
                    : JSON.stringify(articleData, null, 2)
                  }
                </pre>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};